import axios from "axios";
import {
  attachAuthRefreshInterceptor,
  refreshAccessToken,
} from "./authRefreshInterceptor";

// Base URL - Centralized URL configuration
const API_URL = process.env.REACT_APP_API_URL || '';

// Validate in production
if (process.env.NODE_ENV === 'production' && !API_URL) {
  console.error('CRITICAL: REACT_APP_API_URL is not set in production build!');
}

// Initialize Axios instance for auth operations
export const authApiService = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

attachAuthRefreshInterceptor(authApiService);



// Authentication service functions
export const authService = {
  async googleLogin() {
    if (!API_URL) {
      console.error('REACT_APP_API_URL is not configured');
      alert('Configuration error: API URL is not set. Please contact support.');
      return;
    }
    window.location.href = `${API_URL}/auth/google`;
  },
  async login(email, password) {
    try {
      const response = await authApiService.post("/auth/login", {
        email,
        password,
      });

      if (response.data && response.data.statusCode === 200) {
        return {
          success: true,
          data: response.data.body.data,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Login failed",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Login failed",
      };
    }
  },


  async signup(data) {
    try {
      const response = await authApiService.post("/auth/signup", data);

      if (response.data && (response.data.statusCode === 201 || response.data.statusCode === 202)) {
        const body = response.data.body || {};
        return {
          success: true,
          message: body.message || "Signup initiated. Check your email for OTP.",
          userId: body.data?.userId,
        };
      }

      return {
        success: false,
        message: response.data?.body?.message || "Signup failed",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Signup failed",
      };
    }
  },

  async logout() {
    try {
      await authApiService.post("/auth/logout");
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  },


  async refreshToken() {
    try {
      await refreshAccessToken();
      return { success: true, status: 200 };
    } catch (error) {
      return {
        success: false,
        status: error?.status ?? null,
        networkError: !!error?.networkError,
      };
    }
  },

  /**
   * @param {{ skipAuthRefresh?: boolean }} [options]
   * Pass skipAuthRefresh when the caller will refresh explicitly (avoids double /auth/refresh-token).
   */
  async getMe(options = {}) {
    try {
      const response = await authApiService.get("/auth/me", {
        skipAuthRefresh: !!options.skipAuthRefresh,
      });

      if (response.data.statusCode === 200 && response.data?.body?.data) {
        return { success: true, data: response.data.body.data };
      }

      return { success: false, data: null };
    } catch (error) {
      console.warn("getMe error:", error);
      return { success: false, data: null };
    }
  },

  async requestPasswordReset(email) {
    try {
      const response = await authApiService.post("/auth/password-reset/request", {
        emailid: email,
      });

      return {
        success: response.data.statusCode === 200,
        message: response.data.body.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Request failed",
      };
    }
  },

  async resetPassword(token, newPassword, confirmPassword) {
    try {
      const response = await authApiService.post(
        `/auth/password-reset/reset/${token}`,
        {
          newPassword,
          confirmPassword,
        }
      );

      return {
        success: response.data.statusCode === 200,
        message: response.data.body.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Request failed",
      };
    }
  },

  async loginInitiateOtp(email, password) {
    try {
      const response = await authApiService.post("/auth/login/otp/initiate", { email, password });
      const d = response.data;
      if (d?.statusCode === 200) {
        const data = d?.body?.data || {};
        if (data.otpRequired) {
          return { success: true, otpRequired: true, userId: data.userId };
        }
        if (data.totpRequired) {
          return { success: true, totpRequired: true, userId: data.userId };
        }
        if (data.otpBypassed) {
          return { success: true, otpBypassed: true, redirect: data.redirect || "/scribe" };
        }
      }
      return { success: false, message: d?.body?.message || "Unexpected response" };
    } catch (error) {
      return { success: false, message: error.response?.data?.body?.message || "Login failed" };
    }
  },

  async verifyLoginOtp(userId, otp, remember) {
    const response = await authApiService.post("/auth/login/otp/verify", { userId, otp, remember });
    return { success: response.data?.statusCode === 200, message: response.data?.body?.message };
  },

  async verifyLoginTotp(userId, code, remember) {
    try {
      const response = await authApiService.post("/auth/login/totp/verify", { userId, code, remember });
      return { success: response.data?.statusCode === 200, message: response.data?.body?.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.body?.message || "Verification failed" };
    }
  },

  async resendOtp(userId, purpose) {
    try {
      const response = await authApiService.post("/auth/otp/resend", { userId, purpose });
      const data = response.data;
      return {
        success: data?.statusCode === 200,
        message: data?.body?.message || "OTP resent successfully",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.body?.message || "Failed to resend OTP",
      };
    }
  },

  async verifySignupOtp(userId, otp) {
    const response = await authApiService.post("/auth/signup/otp/verify", { userId, otp });
    return { success: response.data?.statusCode === 200, message: response.data?.body?.message };
  },

  async verifyLoginRecoveryCode(userId, code, remember) {
    try {
      const response = await authApiService.post("/auth/login/totp/recovery", { userId, code, remember });
      return {
        success: response.data?.statusCode === 200,
        message: response.data?.body?.message,
        recoveryCodesRemaining: response.data?.body?.data?.recoveryCodesRemaining,
      };
    } catch (error) {
      return { success: false, message: error.response?.data?.body?.message || "Recovery code verification failed" };
    }
  },

  async verifyEmail(token, id) {
    try {
      const response = await authApiService.post("/auth/verify-email", { token, id });
      if (response.data && response.data.statusCode === 200) {
        const isAlreadyVerified = response.data.body?.message === "already_verified";
        return {
          success: true,
          alreadyVerified: isAlreadyVerified || response.data.body?.data?.alreadyVerified || false,
          onboardingComplete: response.data.body?.data?.onboardingComplete ?? false,
          message: response.data.body?.message || "Email verified successfully",
        };
      }
      const isExpired = response.data?.body?.message === "expired";
      return {
        success: false,
        message: isExpired ? "Verification link has expired." : (response.data?.body?.message || "Verification failed"),
        expired: isExpired,
        userId: response.data?.body?.data?.userId,
      };
    } catch (error) {
      const isExpired = error.response?.data?.body?.message === "expired";
      return {
        success: false,
        message: isExpired ? "Verification link has expired." : (error.response?.data?.body?.message || "Verification failed"),
        expired: isExpired,
        userId: error.response?.data?.body?.data?.userId,
      };
    }
  },
};
